import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Global route-change transition: a brief fade on every navigation, so
// moving between pages feels intentional instead of an abrupt content swap.
// Same mechanism InsightsPage already used for its own mount (a setTimeout
// past the reveal, not requestAnimationFrame) — an rAF version was tried
// first and found to flash instead of fade: navigation is triggered from a
// click, a discrete event, and React can flush the effect and its rAF
// callback before the browser ever paints the hidden frame, coalescing
// hidden+visible into one paint. A timeout comfortably longer than a frame
// sidesteps that entirely.
//
// Opacity only — no translateY/transform. A first version slid the content
// up too, but a non-`none` transform on an ancestor becomes the containing
// block for any `position: fixed` descendant, and several pages (Login's
// logged-in "Account" view, RequireSubscription — see CLAUDE.md's "Non-
// Scrollable Pages" pattern) use `position: fixed` for their entire layout,
// anchored to the real viewport via `--topbar-h`/`--layout-sidebar-w`. Any
// transform here made those pages render in the wrong place for the whole
// ~480ms transition before snapping correct — a visible stutter on every
// visit to Account. This pattern is used widely enough that opacity-only is
// the correct fix, not a per-page workaround.
// The marketing pages are standalone light surfaces. Fading between them drops the page to
// opacity 0 and exposes the app's (dark) body background for a beat, which reads as a black
// flash. They swap instantly instead.
const MARKETING_PATHS = ['/', '/academy'];
const isMarketing = (p) => MARKETING_PATHS.includes(p.replace(/\/+$/, '') || '/');

export default function PageTransition({ children }) {
  const { pathname } = useLocation();
  const [renderedPath, setRenderedPath] = useState(pathname);
  const [phase, setPhase] = useState('visible');

  // Reset synchronously during render (not in an effect) so the new page's
  // very first paint is already hidden — otherwise there'd be a one-frame
  // flash of the incoming page at full opacity before the fade-out-and-back-in.
  if (pathname !== renderedPath) {
    setRenderedPath(pathname);
    if (!(isMarketing(pathname) && isMarketing(renderedPath))) setPhase('hidden');
  }

  useEffect(() => {
    if (phase !== 'hidden') return undefined;
    const timer = setTimeout(() => setPhase('visible'), 80);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'visible') return undefined;
    const timer = setTimeout(() => setPhase('done'), 400);
    return () => clearTimeout(timer);
  }, [phase]);

  // "hidden" deliberately carries no `transition` — it must snap to
  // invisible instantly (the same way a freshly-mounted component's first
  // paint just *is* opacity 0, with nothing to animate from). Only "visible"
  // declares the transition, so the one animated leg is hidden -> visible.
  // Giving "hidden" a transition too would animate the done -> hidden snap
  // as an unwanted fade-out, and since it only holds for 80ms before
  // flipping to "visible", that fade-out reverses mid-flight into a wobble
  // instead of a clean fade-in.
  const style =
    phase === 'done'
      ? { opacity: 1 }
      : phase === 'visible'
        ? { opacity: 1, transition: 'opacity 0.4s ease' }
        : { opacity: 0 };

  return <div style={style}>{children}</div>;
}
